import * as prismic from '@prismicio/client';
import { FilledContentRelationshipField, PrismicDocument, Query, RTNode, RTParagraphNode } from '@prismicio/client';

import { Printer } from '@/models/enriched-prismic-documents.ts';
import {
    Fetchers,
    KeyString,
    KeyStringArray,
    PrismicDataType,
    PrismicReturnType,
} from '@/models/prismic-hook-types.ts';
import {
    ConfigAdminGroupsDocument,
    ConfigDcInstanceDocument,
    CustomerConfigDocument,
    HubDocument,
    ProfessionalProfileDocument,
} from '@/models/prismicTypes.ts';

import { client } from '@/services/prismic-service.ts';

import { filterCustomerConfigsByDomain } from '@/utils/customer-utils.ts';
import { getEnvironment } from '@/utils/env-utils.ts';

export enum FeatureKey {
    CONSENT_FORMS = 'feature-consent-forms',
    ANAMNESIS_BUILDER = 'feature-anamnesis-builder',
    CUSTOM_ANAMNESIS = 'feature-custom-anamnesis',
    USER_MANAGEMENT = 'feature-user-management',
    ACCESS_REQUESTS = 'feature-access-requests',
}

const filterByEnvironmentTag = (prismic_data: PrismicDocument[]): PrismicDocument[] => {
    return prismic_data.filter((entry: PrismicDocument) => entry.tags.includes(getEnvironment()));
};

// Default fetcher to use when no custom fetcher is defined
export const defaultFetcher = async <T extends PrismicDataType>(type: T): Promise<PrismicReturnType[T]> => {
    const response = await client.getSingle(type, { lang: 'de-de' });
    console.debug('defaultFetcher call', {
        type,
        response,
    });
    return response.data as PrismicReturnType[T];
};

// Specific fetcher functions for different data types
export const fetchers: Fetchers = {
    [PrismicDataType.CONFIG_ADMIN_GROUPS]: async () => {
        const response: ConfigAdminGroupsDocument = await client.getSingle(PrismicDataType.CONFIG_ADMIN_GROUPS, {
            lang: 'en-us',
        });
        const groups: KeyStringArray = formatAdminGroups(response);

        return groups;
    },
    [PrismicDataType.CUSTOMER_CONFIG]: async () => {
        const response: Query<CustomerConfigDocument> = await client.get({
            filters: [prismic.filter.at('my.customer_config.environment', getEnvironment())],
            lang: 'de-de',
            pageSize: 200,
        });

        const filteredConfigs: CustomerConfigDocument[] = filterCustomerConfigsByDomain(response.results);

        return filteredConfigs.map((config: CustomerConfigDocument) => config.data);
    },
    [PrismicDataType.HUB]: async () => {
        const response: HubDocument[] = await client.getAllByType('hub', { lang: 'de-de' });

        const filteredResponse = filterByEnvironmentTag(response) as HubDocument[];

        const hubsObj: Record<string, HubDocument['data']> = {};
        for (const hub of filteredResponse) {
            hubsObj[hub.id] = hub.data;
        }

        return hubsObj;
    },
    [PrismicDataType.CONFIG_DC_INSTANCE]: async () => {
        const response: ConfigDcInstanceDocument[] = await client.getAllByType('config_dc_instance', {
            lang: 'de-de',
        });

        const filteredConfigs: ConfigDcInstanceDocument[] = filterByEnvironmentTag(
            response
        ) as ConfigDcInstanceDocument[];

        const configsObj: Record<string, ConfigDcInstanceDocument['data'][]> = {};
        for (const config of filteredConfigs) {
            const hubDocumentId: string = (config.data.hub as FilledContentRelationshipField<'hub'>)?.id;
            if (!hubDocumentId) continue;

            if (configsObj[hubDocumentId]) configsObj[hubDocumentId].push(config.data);
            else configsObj[hubDocumentId] = [config.data];
        }

        return configsObj;
    },
    [PrismicDataType.PROFESSIONAL_PROFILE]: async () => {
        const response: ProfessionalProfileDocument[] = await client.getAllByType('professional_profile', {
            lang: 'de-de',
        });

        const filteredProfiles: ProfessionalProfileDocument[] = filterByEnvironmentTag(
            response
        ) as ProfessionalProfileDocument[];

        return new Map(filteredProfiles.map((profile) => [profile.data.key?.[0]?.text ?? '', profile.data]));
    },
    // Add other fetcher functions for different data types here
};

// Type guard to check if a value is a PrismicDataType
export const isPrismicDataType = (value: string): value is PrismicDataType => {
    return Object.values(PrismicDataType).includes(value as PrismicDataType);
};

const formatAdminGroups = (object: ConfigAdminGroupsDocument): KeyStringArray => {
    const formattedGroups: KeyStringArray = {};
    for (const group of object.data.groups) {
        if (!group.group[0]) continue;

        if (
            (getEnvironment() === 'dev' && group.group[0].text.startsWith('prod-')) ||
            (getEnvironment() === 'prod' && group?.group[0].text.startsWith('dev-'))
        ) {
            continue;
        }

        if (!formattedGroups[group.group[0].text]) formattedGroups[group.group[0].text] = [];
        if (group.app_component_key[0]?.text) {
            formattedGroups[group.group[0].text].push(group.app_component_key[0].text);
        }
    }

    return formattedGroups;
};

export const getUserPermissions = (
    feature_access_groups: PrismicReturnType[PrismicDataType.CONFIG_ADMIN_GROUPS],
    user_groups: string[],
    locations: unknown[]
): {
    rendered_pages: KeyString;
    feature_access: KeyString;
} => {
    const renderedPages: KeyString = {};
    const featureAccess: KeyString = {};

    if (!user_groups) {
        return {
            rendered_pages: renderedPages,
            feature_access: featureAccess,
        };
    }

    for (const group of user_groups) {
        if (!feature_access_groups[group]) continue;
        for (const key of feature_access_groups[group]) {
            if (locations.length === 0 && key === 'eterno-today-appointments') continue;
            if (!key.startsWith('feature') && !renderedPages[key]) renderedPages[key] = key;
            if (key.startsWith('feature') && !featureAccess[key]) featureAccess[key] = key;
        }
    }

    return {
        rendered_pages: renderedPages,
        feature_access: featureAccess,
    };
};

export const getTextFromRichTextField = (textField: RTNode[] | RTNode | undefined): string => {
    if (!textField) return '';
    if (Array.isArray(textField)) {
        return (textField as RTParagraphNode[]).map((node) => node.text).join('');
    }

    return (textField as RTParagraphNode).text;
};

export const formatCustomerPrinters = (
    prismic_printers: ConfigDcInstanceDocument['data']['pcs_printers']
): Printer[] => {
    return prismic_printers.map((prismic_printer) => ({
        name: getTextFromRichTextField(prismic_printer.name),
        ip_address: getTextFromRichTextField(prismic_printer.address),
        certificate: getTextFromRichTextField(prismic_printer.certificate),
    }));
};
